<template>
	<main class="c-mcp-authorize">
		<h2>Connect {{ request ? request.clientName : 'your app' }} to Swipekit</h2>
		<p v-if="!isAuth">Sign in to continue connecting your chat app.</p>
		<p v-if="isAuth && currentUser.email">Signed in as <strong>{{ currentUser.email }}</strong>.</p>
		<p v-if="loading">Loading connection request…</p>
		<p v-if="error" role="alert">{{ error }}</p>
		<template v-if="request && !loading">
			<p>
				<strong>{{ request.clientName }}</strong> wants access to
				<strong>{{ request.workspaceName }}</strong
				>.
			</p>
			<p>
				This app can read your saved ads, boards, folders, trackers, usage, and
				AI generation records. It can also save ads, create boards and folders,
				and update tags and board assignments.
			</p>
			<p>
				Access follows your workspace permissions. You can revoke it from the
				MCP page at any time.
			</p>
			<p>
				After approval, you will return to
				<strong>{{ request.redirectHost }}</strong
				>.
			</p>
			<p v-if="request.loopback">
				This connection returns to an app on your computer. Approve only if you
				started it there.
			</p>
			<button class="btn btn-primary" :disabled="saving" @click="decide(true)">
				Allow access
			</button>
			<button class="btn btn-box" :disabled="saving" @click="decide(false)">
				Cancel
			</button>
		</template>
		<p><router-link to="/mcp">Back to MCP settings</router-link></p>
	</main>
</template>

<script>
import { http } from '@swipekit/lib/http';

export default {
	name: 'McpAuthorizePage',
	data: function () {
		return { request: null, loading: false, saving: false, error: '' };
	},
	computed: {
		isAuth: function () {
			return this.$store.getters['users/isAuth'];
		},
		currentUser: function () {
			return this.$store.getters['users/user'];
		},
	},
	watch: {
		isAuth: {
			immediate: true,
			handler: function (value) {
				if (value) {
					this.load();
				}
			},
		},
		'$route.query.request': function () {
			if (this.isAuth) {
				this.load();
			}
		},
	},
	methods: {
		async load() {
			this.request = null;
			this.error = '';
			if (typeof this.$route.query.request !== 'string') {
				this.error = 'Start a new connection from your chat app.';
				return;
			}
			this.loading = true;
			try {
				const response = await http.get(
					`/mcp/oauth/requests/${encodeURIComponent(this.$route.query.request)}`
				);
				this.request = response.data;
			} catch (err) {
				const message = err.response && err.response.data && err.response.data.error;
				this.error = typeof message === 'string' ? message :
					'Could not load this connection request. Start again from your chat app.';
			} finally {
				this.loading = false;
			}
		},
		async decide(allow) {
			this.saving = true;
			this.error = '';
			try {
				const response = await http.post(
					`/mcp/oauth/requests/${encodeURIComponent(
						this.$route.query.request
					)}`,
					{ allow },
					{ headers: { 'X-MCP-CSRF': this.request.csrf } }
				);
				window.location.assign(response.data.redirectUrl);
			} catch (err) {
				this.error =
					'Could not finish connecting. Start again from your chat app.';
				this.saving = false;
			}
		},
	},
};
</script>

<style lang="scss">
.c-mcp-authorize {
	max-width: 440px;
	margin: 3rem auto;
	padding: 1.25rem;
	background: var(--color-bg-1);
	border: 1px solid var(--color-bg-5);
	border-radius: 0.5rem;
	h2 {
		text-align: center;
	}
	p {
		margin: 1rem 0;
	}
	button {
		margin: 0 0.5rem 1rem 0;
	}
}
</style>
